import { TableField } from "../../types/interfaces";
import { customFormatDate } from "../../utils/validators";

/**
 * Determines the most appropriate Ag-Grid cell editor to use
 * based on a field's data type, list values, and other metadata.
 *
 * @param field TableField object containing metadata.
 * @returns The name of the cell editor to be used in Ag-Grid.
 */
export function getEditorForField(field: TableField): string {
    const type = (field.dataType || "").toLowerCase();

    // === Read-only fields shouldn't have editors
    if (field.readOnly) {
        return "agTextCellEditor";
    }

    // === Dropdown editor if static list of values is provided
    if (field.listValues) {
        return "agSelectCellEditor";
    }

    // === Field-specific types
    switch (type) {
        case "boolean":
        case "bool":
            return "agCheckboxCellEditor";

        case "int":
        case "integer":
        case "number":
        case "decimal":
        case "float":
        case "currency":
            return "agNumberCellEditor";

        case "date":
            return "agDateCellEditor";

        case "datetime":
        case "timestamp":
            return "agTextCellEditor"; // Custom one later (datetime-local picker)

        case "time":
            return "agTextCellEditor"; // Could become custom too

        case "email":
        case "phone":
        case "text":
        case "string":
        default:
            return "agTextCellEditor";
    }
}

/**
 * Extracts a list of dropdown values (if defined) for a given field.
 * These can be injected into the `cellEditorParams` for Ag-Grid Selects.
 *
 * @param field TableField
 * @returns Array of string values or null
 */
export function getDropdownValues(field: TableField): string[] | null {
    if (!field.listValues) return null;
    return field.listValues.split(",").map(s => s.trim()).filter(Boolean);
}

/**
 * Utility for formatting values for display based on field type.
 * (This isn't directly editing, but often used with cellRenderer)
 */
export function formatFieldValue(value: any, field: TableField): string {
    if (value === null || value === undefined || value === "") return "";

    const type = (field.dataType || "").toLowerCase();
    const format = field.format?.trim();
    const inputMask = field.inputMask?.trim();

    try {
        // === Boolean fields ===
        if (type === "boolean" || type === "bool") {
            const val = String(value).toLowerCase();
            return ["1", "true", "yes"].includes(val) ? "✅" : "❌";
        }

        // === Date/timestamp formatting ===
        if (["date", "datetime", "timestamp"].includes(type)) {
            const date = new Date(value);
            if (isNaN(date.getTime())) return "";

            if (format?.toLowerCase().includes("yyyy") || format?.toLowerCase().includes("mm")) {
                return customFormatDate(date, format);
            }

            return date.toLocaleDateString();
        }

        // === Time-only
        if (type === "time") {
            const d = new Date(`1970-01-01T${value}`);
            return isNaN(d.getTime()) ? value : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        }

        // === Currency / Number
        if (type === "currency") {
            const num = parseFloat(value);
            return isNaN(num)
                ? String(value)
                : num.toLocaleString(undefined, { style: "currency", currency: "USD" });
        }

        if (type === "float" || type === "decimal") {
            return Number(value).toLocaleString(undefined, { maximumFractionDigits: 6 });
        }

        if (type === "number" || type === "int" || type === "integer") {
            return Number(value).toLocaleString();
        }

        // === File/blob ===
        if (field.isBlob) {
            return value ? "View" : ""; // We'll handle linking outside this util
        }

        // === Text (default) ===
        let display = String(value);

        // Trim to length if needed
        if (field.length && display.length > field.length) {
            display = display.slice(0, field.length) + "…";
        }

        // Enforce special character restriction if set
        if (field.allowSpecialCharacters === false) {
            display = display.replace(/[^a-zA-Z0-9 ]/g, "");
        }

        return display;
    } catch (err) {
        return String(value);
    }
}
